/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React from "react";
import { TextField, Typography, Button } from "@mui/material";
import { useUpdateComponent } from "cms/components/hooks/use-update-component";
import LabelledField from "components/labelled-field";
import { SimplicityComposerEnsureTipTap } from "cms/rich-text/simplicity-composer-tiptap";
import { LinkSelectorWrapper } from "cms/links/link-selector/link-selector-wrapper";
import { ImageSelectorWrapper } from "cms/images/image-selector/image-selector-wrapper";

const SectionLinkCardsComponentEditor = () => {
    const componentData = useUpdateComponent();
    const component = componentData?.component;
    const update = componentData?.update;
    
    const title = component?.title;
    const description = component?.description;
    const cards = component?.cards || [];

    const handleAddCard = () => {
        const newCard = {
            icon: null,
            link: null
        };
        const updatedCards = [...(cards || []), newCard];
        update("cards", updatedCards);
    };

    const handleRemoveCard = (index) => {
        const updatedCards = (cards || []).filter((_, i) => i !== index);
        update("cards", updatedCards);
    };

    const handleUpdateCard = (index, field, value) => {
        const updatedCards = (cards || []).map((card, i) => 
            i === index ? { ...card, [field]: value } : card
        );
        update("cards", updatedCards);
    };

    const handleLinkSet = (cardIndex, selectedLink) => {
        handleUpdateCard(cardIndex, "link", selectedLink);
    };

    const handleLinkChange = (cardIndex, selectedLink) => {
        const currentCard = cards?.[cardIndex];
        const updatedCard = { 
            ...currentCard, 
            link: selectedLink
        };
        const updatedCards = (cards || []).map((card, i) => 
            i === cardIndex ? updatedCard : card
        );
        update("cards", updatedCards);
    };

    const handleLinkRemove = (cardIndex) => {
        const currentCard = cards?.[cardIndex];
        const updatedCard = { 
            ...currentCard, 
            link: null
        };
        const updatedCards = (cards || []).map((card, i) => 
            i === cardIndex ? updatedCard : card
        );
        update("cards", updatedCards);
    };

    const handleImageChange = (cardIndex, selectedImage) => {
        handleUpdateCard(cardIndex, "icon", selectedImage);
    };

    const handleImageSave = (cardIndex, savedImage) => {
        handleUpdateCard(cardIndex, "icon", savedImage);
    };

    const handleImageRemove = (cardIndex) => {
        handleUpdateCard(cardIndex, "icon", null);
    };

    const handleImageReload = (cardIndex, reloadedImage) => {
        handleUpdateCard(cardIndex, "icon", reloadedImage);
    };

    const handleTitleChange = (event) => {
        update("title", event?.target?.value);
    };

    const handleDescriptionChange = (richText) => {
        update("description", richText);
    };

    const createLinkSetHandler = (cardIndex) => {
        return (selectedLink) => handleLinkSet(cardIndex, selectedLink);
    };

    const createLinkChangeHandler = (cardIndex) => {
        return (selectedLink) => handleLinkChange(cardIndex, selectedLink);
    };

    const createLinkRemoveHandler = (cardIndex) => {
        return () => handleLinkRemove(cardIndex);
    };

    const createImageChangeHandler = (cardIndex) => {
        return (selectedImage) => handleImageChange(cardIndex, selectedImage);
    };

    const createImageSaveHandler = (cardIndex) => {
        return (savedImage) => handleImageSave(cardIndex, savedImage);
    };

    const createImageRemoveHandler = (cardIndex) => {
        return () => handleImageRemove(cardIndex);
    };

    const createImageReloadHandler = (cardIndex) => {
        return (reloadedImage) => handleImageReload(cardIndex, reloadedImage);
    };

    return (
        <div className="simplicity-section-link-cards-editor">
            <div className="simplicity-section-link-cards-editor-general">
                <div className="simplicity-section-link-cards-editor-general-container">
                    <div className="simplicity-section-link-cards-editor-general-container-item">
                        <LabelledField label="Title">
                            <TextField
                                fullWidth
                                value={title || ""}
                                onChange={handleTitleChange}
                                placeholder="Enter title"
                                variant="outlined"
                            />
                        </LabelledField>
                    </div>
                    
                    <div className="simplicity-section-link-cards-editor-general-container-item">
                        <LabelledField label="Description">
                            <SimplicityComposerEnsureTipTap
                                richtext={description}
                                onChange={handleDescriptionChange}
                            />
                        </LabelledField>
                    </div>
                </div>
            </div>
            
            <div className="simplicity-section-link-cards-editor-add-card">
                <Button 
                    variant="outlined" 
                    onClick={handleAddCard}
                    color="primary"
                >
                    Add Card
                </Button>
            </div>

            {(cards || []).map((card, index) => {
                return (
                    <div key={index} className="simplicity-section-link-cards-editor-card">
                        <div className="simplicity-section-link-cards-editor-card-header">
                            <Typography variant="subtitle1">
                                Card {index + 1}
                            </Typography>
                            <Button 
                                variant="outlined" 
                                color="error" 
                                size="small"
                                onClick={() => handleRemoveCard(index)}
                            >
                                Remove
                            </Button>
                        </div>
                        
                        <div className="simplicity-section-link-cards-editor-card-fields">
                            <div className="simplicity-section-link-cards-editor-card-field">
                                <LabelledField label="Link">
                                    <LinkSelectorWrapper
                                        link={card?.link}
                                        setLink={createLinkSetHandler(index)}
                                        onLinkChange={createLinkChangeHandler(index)}
                                        onLinkRemove={createLinkRemoveHandler(index)}
                                    />
                                </LabelledField>
                            </div>
                            
                            <div className="simplicity-section-link-cards-editor-card-field">
                                <LabelledField label="Icon">
                                    {card && (
                                        <ImageSelectorWrapper
                                            image={card?.icon}
                                            onImageChange={createImageChangeHandler(index)}
                                            onImageSave={createImageSaveHandler(index)}
                                            onImageRemove={createImageRemoveHandler(index)}
                                            onReload={createImageReloadHandler(index)}
                                        />
                                    )}
                                </LabelledField>
                            </div>
                        </div>
                    </div>
                );
            })}

            {(cards || []).length === 0 && (
                <div className="simplicity-section-link-cards-editor-empty-state">
                    <Typography variant="body2" color="text.secondary">
                        No cards added yet. Click &quot;Add Card&quot; to get started.
                    </Typography>
                </div>
            )}
        </div>
    );
};

SectionLinkCardsComponentEditor.displayName = "SectionLinkCardsComponentEditor";
export { SectionLinkCardsComponentEditor };
